/**
 * MarketingActivityAudit — W48 Marketing Dashboard Component (E)
 *
 * Demo Marketing's weekly write log table.
 * Columns: date, type, account_id, account_name, theme, status
 *
 * account_id / account_name shown as em-dash where marketing_events has no
 * account_id column (W47 known gap). Do not editorialize.
 *
 * No Recharts. Clean HTML table with Tailwind + canon palette.
 */

import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";

interface AuditRow {
  date: string;
  type: string;
  account_id: string;
  account_name: string;
  theme: string;
  status: string;
}

interface AuditData {
  rows: AuditRow[];
  marketing_lead_id: string | null;
  fallback: boolean;
}

function formatDate(dateStr: string): string {
  if (dateStr === "—") return "—";
  try {
    return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

const STATUS_PILL: Record<string, { bg: string; text: string }> = {
  active:    { bg: "#DCFCE7", text: "#15803D" },
  planned:   { bg: "#FEF3C7", text: "#92400E" },
  completed: { bg: "#F5F5F4", text: "#6B7280" },
  event:     { bg: "#EFF6FF", text: "#0B0B0F" },
  draft:     { bg: "#FEF9C3", text: "#854D0E" },
};

function StatusPill({ status }: { status: string }) {
  const s = status?.toLowerCase() ?? "planned";
  const style = STATUS_PILL[s] ?? { bg: "#F5F5F4", text: "#6B7280" };
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {status}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-8 bg-[hsl(var(--surface-2))] rounded mb-2" />
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-10 bg-[hsl(var(--surface-2))] rounded mb-1" />
      ))}
    </div>
  );
}

export function MarketingActivityAudit() {
  const session = getSession();
  const [data, setData] = useState<AuditData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = session?.token;
    if (!token) { setError("Not authenticated"); setLoading(false); return; }

    fetch("/api/dashboards/marketing/audit", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => {
        console.error("[MarketingActivityAudit]", e);
        setError("Could not load audit data");
        setLoading(false);
      });
  }, [session?.token]);

  if (loading) return <Skeleton />;
  if (error) return <p className="text-[12px] text-[#DC2626]">{error}</p>;

  const rows = data?.rows ?? [];

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-6 text-center">
        <p className="text-[13px] text-muted-foreground">
          No activity logged in the last 4 weeks.
        </p>
        {data?.fallback && (
          <p className="mt-1 text-[11px] text-muted-foreground italic">
            Demo Marketing not found in tenant users. Showing all recent marketing events.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {data?.fallback && (
        <p className="text-[11px] text-muted-foreground italic px-1">
          Demo Marketing user record not found. Showing all recent marketing events (last 4 weeks).
        </p>
      )}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-[hsl(var(--surface-2))] border-b border-border">
              <tr>
                {["Date", "Type", "Account ID", "Account", "Theme", "Status"].map((col) => (
                  <th
                    key={col}
                    className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row, i) => (
                <tr
                  key={i}
                  className="hover:bg-[hsl(var(--surface-2))] transition-colors"
                >
                  <td className="px-3 py-2.5 tabular-nums text-muted-foreground whitespace-nowrap">
                    {formatDate(row.date)}
                  </td>
                  <td className="px-3 py-2.5 capitalize text-foreground">
                    {row.type}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
                    {row.account_id}
                  </td>
                  <td className="px-3 py-2.5 text-foreground max-w-[140px] truncate">
                    {row.account_name}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground max-w-[140px] truncate">
                    {row.theme}
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusPill status={row.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground px-1">
        Account ID and account name shown as — where marketing_events has no account_id column (W47 known gap).
      </p>
    </div>
  );
}
